import Axios from 'axios';
import {
  clearString,
  getSlugFromIDs,
  notification,
  pageHasContent,
  TokenExpiredNotification,
} from '../assets/constants/strings';
import config from '../config';
import store from '../store';
import {
  CONFIRM_EMAIL,
  CREATE_BOOKMARK,
  DELETE_BOOKMARK,
  DELETE_BOOKMARKS,
  GET_BOOKMARKS,
  LOG_IN,
  LOG_OUT,
  REGISTER,
  RESEND_EMAIL,
  RESET_PASS,
  UPDATE_PASS,
  UPDATE_USER,
  INVITE_USER,
  INVITE_USER_ERROR,
  GET_USERS_BY_CLIENT_ID,
  DELETE_USER_CLIENT,
  UPDATE_CLIENT_ROLE,
  REINVITE_USER,
  REINVITE_USER_ERROR,
  SEND_ENQUIRY,
  SUBSCRIPTION_UPDATE,
  CREATE_CLIENT_SUBSCRIPTION,
  GET_USER_PLANS,
  UPDATE_USER_USAGE,
  GET_USER_USAGE,
} from '../store/User/userTypes';
import {
  API_BASE,
  API_CLIENT,
  API_DOCUMENTS,
  // API_DOCUMENT_BY_ID,
  API_GLOSSARY,
  API_GLOSSARY_RELATIONSHIP,
  API_LEVEL_FOUR,
  API_LEVEL_ONE,
  API_LEVEL_THREE,
  API_LEVEL_TWO,
  API_LOGO,
  API_NAVIGATION,
  API_PAGES_BY_DOC,
  API_RELATED_CONTENT,
  API_RELATED_CONTENT_RELATIONSHIP,
  API_RELATED_DOCUMENT,
  API_RELATED_SECTION,
  API_PAGES,
  AUTHENTICATE,
  CONFIRM_EMAIL as CONFIRM_EMAIL_ENDPOINT,
  CREATE_BOOKMARK_API,
  DELETE_BOOKMARKS_API,
  DELETE_BOOKMARK_API,
  FORGOT_PASSWORD,
  GET_USER_BOOKMARKS,
  INTERNAL_ERROR,
  REGISTER as REGISTER_API,
  RESEND_EMAIL_API,
  RESET_PASSWORD,
  UPDATE_USER_API,
  CHANGE_PASSWORD_API,
  REFRESH_TOKEN_API,
  LOG_OUT_API,
  CANCEL_REASON_API,
  API_SEARCH,
  CREATE_COMMENT_API,
  API_GET_LIST_BY_ID,
  API_DOCUMENT_BY_SLUG,
  API_DOCUMENT_BY_ID,
  API_DOCUMENT_BY_NEW_VERSION_ID,
  // GET_CLIENT_PRODUCT_BY_ID_API,
  INVITE_USER_API,
  GET_USERS_BY_CLIENT_ID_API,
  DELETE_USER_CLIENT_API,
  UPDATE_CLIENT_ROLE_API,
  REINVITE_USER_API,
  SEND_ENQUIRY_API,
  API_GET_DOC_SUMMARY,
  UPDATE_CLIENT_API,
  CREATE_CLIENT_SUBSCRIPTION_API,
  UPDATE_CLIENT_SUBSCRIPTION_API,
  GET_PLANS,
  USAGE_BY_USER_ID_API,
  UPDATE_USER_USAGE_API,
  GET_QUERY_BY_USER_ID_API,
  GET_QUERY_MESSAGES_BY_QUERY_ID_API,
  CREATE_NEW_QUERY_API,
  UPDATE_QUERY_API,
  DELETE_QUERY_API,
  CREATE_NEW_MESSAGE_API,
  UPDATE_MESSAGES_RESPONSE_API,
  BUY_ADDITIONAL_QUERIES_API,
} from './consts';
import { GET_MESSAGES, GET_QUERIES } from '../store/Revealr/revealrTypes';
import { BUY_ADDITIONAL_QUERIES } from '../store/Client/clientTypes';

const BASE_URL = config.baseURL;
const AUTH_BASE = config.authBaseURL;
const IMAGES_FOLDER = config.ImagesFolder;

/**
 * Base json type header for all Axios calls.
 */
const HEADER = {
  'Content-Type': 'application/json',
};

/**
 * SharePoint calls auth header
 * @param {string} spKey
 * @returns Header with sharepoint key.
 */
const SP_HEADER = (spKey) => ({
  ...HEADER,
  spKey: spKey,
});

/**
 * Swagger.API calls auth header
 * @param {string} token
 * @returns Header with token.
 */
const AUTH_HEADER = (token) => ({
  ...HEADER,
  Authorization: `Bearer ${token}`,
});

const axios = Axios.create({
  BASE_URL,
  withCredentials: true,
});

const loadText = (text) => {
  if (document.getElementById('extra-load-text'))
    document.getElementById('extra-load-text').innerText = text;
};

//#region Client Reducer

/**
 * Fetches a level list
 * @param {string} level The list to be fetched (levels one through four)
 * @returns The level list according to the level param
 */
export const getClientLevelList = async (level, key) => {
  try {
    loadText('Regulations...');
    const res = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + level,
      headers: SP_HEADER(key),
    });

    const data = res.data.data;

    return data && data.length > 0
      ? data
          .map((level) => ({
            ID: Number(level.id),
            Title: level.title,
            Sort: level.sort,
          }))
          .sort((a, b) => (a.Sort > b.Sort ? 1 : -1))
      : [];
  } catch (error) {
    console.log('EXC in getClientLevelList\n', error.message);
    return [];
  }
};

/**
 * Fetches the organisation structure
 * @param {string} key SharePoint access key.
 * @returns The organisation structure.
 */
export const getClientStructure = async (key) => {
  try {
    loadText('Document Structures...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_CLIENT,
      headers: SP_HEADER(key),
    });

    const data = apiResponse.data.data;

    return data && data.length > 0
      ? {
          LevelOne: data[0].levelOne,
          LevelTwo: data[0].levelTwo,
          LevelThree:
            data[0].levelThree && data[0].levelThree.length > 0
              ? data[0].levelThree
              : null,
          LevelFour:
            data[0].levelFour && data[0].levelFour.length > 0
              ? data[0].levelFour
              : null,
        }
      : [];
  } catch (error) {
    console.log('EXC in getClientStructure', error.message);
    return [];
  }
};

export const getLogo = async (key) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_LOGO,
      headers: SP_HEADER(key),
    });
    if (apiResponse) return apiResponse.data.data.logo;
    else return '';
  } catch (error) {
    console.log('EXC in getLogo', error.message);
    return '';
  }
};

/**
 * Assembles the organizing structure of a document
 * @param {*} document The document to be constructed
 * @returns {Promise<{
 * levelOne: { ID: number, Title: string, Sort: number},
 * levelTwo: { ID: number, Title: string, Sort: number},
 * levelThree?: { ID: number, Title: string, Sort: number},
 * levelFour?: { ID: number, Title: string, Sort: number}
 * }>} The document's organization structures
 */
const getDocumentLevels = async (document) => {
  try {
    const client = store.getState().client;
    const spKey = store.getState().user.user.spKey;

    let levelsObj = {};

    if (client.levelOneList && client.levelOneList.length > 0) {
      levelsObj.levelOne = client.levelOneList.filter(
        (lvl1) => lvl1.ID === Number(document.level_one_id)
      )[0];
    } else {
      levelsObj.levelOne = await getDocumentLevelById(
        Number(document.level_one_id),
        API_LEVEL_ONE,
        spKey
      );
    }

    if (client.levelTwoList && client.levelTwoList.length > 0) {
      levelsObj.levelTwo = client.levelTwoList.filter(
        (lvl2) => lvl2.ID === Number(document.level_two_id)
      )[0];
    } else {
      levelsObj.levelTwo = await getDocumentLevelById(
        Number(document.level_two_id),
        API_LEVEL_TWO,
        spKey
      );
    }

    // optional level
    if (document.level_three_id) {
      if (client.levelThreeList && client.levelThreeList.length > 0) {
        levelsObj.levelThree = client.levelThreeList.filter(
          (lvl3) => lvl3.ID === Number(document.level_three_id)
        )[0];
      } else {
        levelsObj.levelThree = await getDocumentLevelById(
          Number(document.level_three_id),
          API_LEVEL_THREE,
          spKey
        );
      }
    } else levelsObj.levelThree = null;

    // optional level
    if (document.level_four_id) {
      if (client.levelFourList && client.levelFourList.length > 0) {
        levelsObj.levelFour = client.levelFourList.filter(
          (lvl4) => lvl4.ID === Number(document.level_four_id)
        )[0];
      } else {
        levelsObj.levelFour = await getDocumentLevelById(
          Number(document.level_four_id),
          API_LEVEL_FOUR,
          spKey
        );
      }
    } else levelsObj.levelFour = null;

    return levelsObj;
  } catch (e) {
    console.log('Error while trying to fetch levels', e);
    return null;
  }
};

/**
 * Fetch documents from WorkingDocuments list
 * @param {string} key SharePoint Access key.
 * @returns The array of published documents
 */
export const getClientDocuments = async (key) => {
  try {
    loadText('Loading RegulationCity...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_DOCUMENTS,
      headers: SP_HEADER(key),
    });

    const data = apiResponse.data.data;

    // TODO INVESTIGATE IF FASTER LIKE THIS OR WITH HOOKS
    // ON FRONTEND TO DETERMINE IF LISTS ARE POPULATED

    const availableDocs = data && data.length > 0 ? data : [];

    const documents = [];

    for (const document of availableDocs) {
      // finding duplicate slugs
      // if (publishedDocs.some((doc) => doc.slug === document.slug && doc.id !== document.id)) {
      //   console.log('\n\n\n duplicates:', publishedDocs.filter((doc) => doc.slug === document.slug));
      // }

      // console.log(document.slug);

      const levels = await getDocumentLevels(document);

      if (!levels || !levels.levelOne || !levels.levelTwo) {
        // console.log('Mandatory Levels not found for document', document);
        console.log('Mandatory Levels not found for document');
      } else {
        documents.push({
          id: document.doc_id,
          title: document.title,
          slug: document.slug,
          fileName: document.filename,
          levelOne: levels.levelOne,
          levelTwo: levels.levelTwo,
          levelThree: levels.levelThree,
          levelFour: levels.levelFour,
          version:
            !document.document_version || document.document_version === '-'
              ? null
              : Number(document.document_version),
          customTags: document.keywords ? document.keywords.split(',') : [],
          status: document.status,
          // versioning
          newVersionId: document.new_version_id,
          previousVersionsList: [],
          supersededDate: document.supersededDate
            ? new Date(document.supersededDate)
            : null,
          // publishedDate: new Date(document.PublishedDate),
          // regCity specific
          versionDate: document.version_date
            ? new Date(document.version_date)
            : null,
          registrationDate: document.registration_date
            ? new Date(document.registration_date)
            : null,
          createdDate: document.created ? new Date(document.created) : null,
        });
      }
    }

    // iterate through superseded documents
    documents
      .filter((Doc) => Doc.newVersionId !== null && Doc.status === 'Superseded')
      .forEach((Doc) => {
        // locate its new version document in list
        const parentItem = documents.filter(
          (newVersionDoc) => newVersionDoc.id === Doc.newVersionId
        )[0];

        if (parentItem) {
          // find its index
          const parentIndex = documents.indexOf(parentItem);
          // insert
          documents[parentIndex].previousVersionsList.push(Doc);
          documents[parentIndex].previousVersionsList.sort((a, b) =>
            a.supersededDate < b.supersededDate ? 1 : -1
          );
        }
      });
    return documents.filter((doc) => doc.status === 'Published');
  } catch (error) {
    console.log('EXC in getClientDocuments', error.message);
    return [];
  }
};

export const getDocsBookmarks = async (key) => {
  try {
    loadText('Loading RegulationCity...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_DOCUMENTS,
      headers: SP_HEADER(key),
    });
    const data = apiResponse.data.data;
    const availableDocs = data && data.length > 0 ? data : [];
    const documents = [];

    for (const document of availableDocs) {
      const levels = await getDocumentLevels(document);

      if (!levels || !levels.levelOne || !levels.levelTwo) {
        // console.log('Mandatory Levels not found for document', document);
        console.log('Mandatory Levels not found for document');
      } else {
        documents.push({
          id: document.doc_id,
          title: document.title,
          slug: document.slug,
          levelOne: levels.levelOne,
          levelOneTitle: levels.levelOne.Title,
          levelTwoTitle: levels.levelTwo.Title,
          status: document.status,
          // versioning
          versionDate: document.version_date
            ? new Date(document.version_date)
            : null,
          newVersionId: document.new_version_id,
          previousVersionsList: [],
          supersededDate: document.supersededDate
            ? new Date(document.supersededDate)
            : null,
        });
      }
    }
    return documents;
  } catch (error) {
    console.log('Error in get bookmarks documents', error.message);
    return [];
  }
};

// const duplicateSlugRegex = new RegExp(/(-\d+)/gmi);

export const getClientPages = async (key) => {
  try {
    // loadText('All pages...');
    const res = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_PAGES,
      headers: SP_HEADER(key),
    });

    const data = res.data.data;

    let documents = store.getState().client.documents;
    if (!documents) documents = await getClientDocuments();

    return data && data.length > 0
      ? data
          .filter(
            (page) =>
              documents.filter((doc) => doc.id === page.doc_id).length > 0
          )
          .map((page) => {
            // detecting duplicate slugs
            // if (data.some((PAGE) => PAGE.slug === page.slug && PAGE.id !== page.id && PAGE.doc_id === page.doc_id)) {
            //   console.log(
            //     'doc:', page.doc_name, '\n',
            //     'pages:',
            //     data.filter((Page) => Page.slug === page.slug && Page.doc_id === page.doc_id).map((p) => p.title)
            //   );
            // console.log('\n\npage', page.title);
            // data
            //   .filter((p) => p.slug === page.slug && p.doc_id === page.doc_id)
            //   .forEach((pageWithIndexedSlug, index) => {
            //     // add indexes from the second one forward
            //     if (index > 0) {
            //       // find elemnent in whole array
            //       const i = data.findIndex(x => x.id === pageWithIndexedSlug.id);
            //       // construct string
            //       const newSlug = `${data[i].slug}-${index}`;
            //       console.log('page', pageWithIndexedSlug, 'now has slug', newSlug);
            //       // insert at right position
            //       data[i].slug = newSlug;
            //     }
            //   });
            // }

            if (page.content.match('<img')) {
              // replace images location
              page.content = page.content.replace(
                new RegExp(clearString(IMAGES_FOLDER), 'gi'),
                `${config.WordPressURL}wp-content/uploads/2022/03/`
              );
            }

            return {
              id: page.page_id,
              title: page.title,
              slug: page.slug,
              parentId:
                page.page_parent_id && page.page_parent_id.length > 0
                  ? page.page_parent_id
                  : null,
              tags: page.tags,
              documentName: page.doc_name,
              documentId: page.doc_id,
              documentSlug: documents.filter((doc) => doc.id === page.doc_id)[0]
                ? documents.filter((doc) => doc.id === page.doc_id)[0].slug
                : null,
              HTMLContent: page.content.replace(/href="#/gi, 'href="/app'),
            };
          })
      : [];
  } catch (error) {
    console.log('EXC in getPages\n', error.message);
    return [];
  }
};

/**
 *
 * @param {{singular: string,plural: string,subString: boolean}} searchObj
 * @param {string} key
 * @returns
 */
export const search = async (searchObj, key) => {
  try {
    if (!key) key = store.getState().user.user.spKey;
    // console.log('searching', searchObj, key);
    const res = await axios.post(
      BASE_URL + API_SEARCH,
      JSON.stringify(searchObj),
      {
        headers: SP_HEADER(key),
      }
    );

    const data = res.data.data;

    // console.log('found', data);

    let documents = store.getState().client.documents;
    if (!documents) documents = await getClientDocuments();

    return data && data.length > 0
      ? data
          .filter(
            (page) =>
              documents.filter((doc) => doc.id === page.doc_id).length > 0
          )
          .map((page) => {
            if (
              typeof page.content === 'string' &&
              page.content.match('<img')
            ) {
              // replace images location
              page.content = page.content.replace(
                new RegExp(clearString(IMAGES_FOLDER), 'gi'),
                `${config.WordPressURL}wp-content/uploads/2022/03/`
              );
            }

            const doc = documents.filter((doc) => doc.id === page.doc_id)[0];

            return {
              id: page.page_id,
              title: page.title,
              slug: page.slug,
              parentId:
                page.page_parent_id && page.page_parent_id.length > 0
                  ? page.page_parent_id
                  : null,
              tags: page.tags,
              documentName: page.doc_name,
              documentId: page.doc_id,
              documentSlug: doc.slug ? doc.slug : null,
              HTMLContent:
                typeof page.content === 'string' &&
                page.content.replace(/href="#/gi, 'href="/app'),
              // document attributes
              versionDate: doc.versionDate,
              levelOne: doc.levelOne.Title,
              levelTwo: doc.levelTwo.Title,
              levelThree: doc.levelThree ? doc.levelThree.Title : null,
              levelFour: doc.levelFour ? doc.levelFour.Title : null,
            };
          })
      : [];
  } catch (error) {
    console.log('EXC in search\n', error.message);
    return [];
  }
};

export const getClientRightNavigation = async (key) => {
  try {
    loadText('Navigation Items...');
    const res = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_NAVIGATION,
      headers: SP_HEADER(key),
    });

    const data = res.data.data;

    if (data) return data[0].title.split(',');
    else return [];
  } catch (error) {
    console.log('EXC in GetRightNavContent', error.message);
    return [];
  }
};

export const getGlossaries = async (key) => {
  try {
    loadText('Glossary...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_GLOSSARY,
      headers: SP_HEADER(key),
    });

    const data = apiResponse.data.data;

    if (data && data.length > 0) {
      const glossaryList = [];

      data.forEach((glossaryObj) => {
        if (glossaryObj.glossary_content) {
          const contentAsList = glossaryObj.glossary_content
            .replace(/{|}/g, '')
            .split(`","`);

          const contentArray = [];
          contentAsList.forEach((item, i) => {
            if (
              !(
                i === 0 &&
                item.split(`":"`)[0].match('Term') &&
                item.split(`":"`)[1].match('Definition')
              )
            )
              contentArray.push({
                Key: item.split(`":"`)[0].replace(/"|{|}/g, ''),
                Definition: item
                  .split(`":"`)[1]
                  .replace(/"|{|}/g, '')
                  .replace(/\\t/g, '&emsp;'),
              });
          });
          glossaryList.push({
            ID: Number(glossaryObj.id),
            GlossaryName: glossaryObj.glossary_name,
            Content: contentArray,
          });
        }
      });

      return glossaryList;
    } else {
      return [];
    }
  } catch (error) {
    console.log('EXC in getGlossary', error.message);
    return [];
  }
};

// Get Glossary relationships
export async function getGlossaryRelationships(glossaries, key) {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_GLOSSARY_RELATIONSHIP,
      headers: SP_HEADER(key),
    });

    const data = apiResponse.data.data;

    let relationshipsArray = [];

    if (data && data.length > 0) {
      try {
        relationshipsArray =
          data && data.length > 0
            ? data.map((relationship) => ({
                GlossaryId: Number(relationship.glossary_id),
                TargetType: relationship.target_type,
                TargetValue: relationship.target_value,
              }))
            : [];
      } catch (e) {
        relationshipsArray =
          data && data.length > 0
            ? data.map((relationship) => ({
                GlossaryId: Number(relationship.glossary_id),
                TargetType: relationship.target_type,
                TargetValue: relationship.target_value,
              }))
            : [];
      }
    }

    // mapping the relationships to the items
    return relationshipsArray.length > 0
      ? glossaries.map((glossary) => ({
          ...glossary,
          Relationships: relationshipsArray
            .filter((relationship) => relationship.GlossaryId === glossary.ID)
            .map((relationship) => ({
              ...relationship,
              TargetValue:
                // document ids are strings, while level ids are numbers
                relationship.TargetType === 'Document' ||
                relationship.TargetType === 'Global'
                  ? relationship.TargetValue
                  : Number(relationship.TargetValue),
            })),
        }))
      : [];
  } catch (error) {
    console.log('getRelatedContents ERROR', error);
    return [];
  }
}

// Get Related Contents
export async function getRelatedContents(key) {
  try {
    loadText('Your Related Content...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_RELATED_CONTENT,
      headers: SP_HEADER(key),
    });

    const data = apiResponse.data.data;

    try {
      return data
        ? data.map((content) => ({
            ID: Number(content.id),
            Title: content.title,
            UploadContent: content.upload_content,
            ContentType: content.content_type,
            RelatedContentUrl: content.related_content_url,
          }))
        : [];
    } catch (e) {
      return data
        ? data.map((content) => ({
            Title: content.title,
            UploadContent: content.upload_content,
            ContentType: content.content_type,
            RelatedContentUrl: content.related_content_url,
          }))
        : [];
    }
  } catch (error) {
    console.log('getRelatedContents ERROR', error);
    return [];
  }
}

// Get Related Content relationships
export async function getRelatedContentsRelationships(contents, key) {
  try {
    // loadText('Related Content Structures...');

    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_RELATED_CONTENT_RELATIONSHIP,
      headers: SP_HEADER(key),
    });

    const data = apiResponse.data.data;

    let relationshipsArray = [];

    if (data && data.length > 0) {
      try {
        relationshipsArray =
          data && data.length > 0
            ? data.map((relationship) => ({
                RelatedContentId: Number(relationship.relationship_content_id),
                TargetType: relationship.target_type,
                TargetValue: relationship.target_value,
              }))
            : [];
      } catch (e) {
        relationshipsArray =
          data && data.length > 0
            ? data.map((relationship) => ({
                RelatedContentId: Number(relationship.relationship_content_id),
                TargetType: relationship.target_type,
                TargetValue: relationship.target_value,
              }))
            : [];
      }
    }
    // mapping the relationships to the items
    return relationshipsArray.length > 0
      ? contents.map((content) => ({
          ...content,
          Relationships: relationshipsArray
            .filter(
              (relationship) => relationship.RelatedContentId === content.ID
            )
            .map((relationship) => ({
              ...relationship,
              TargetValue:
                // document ids are strings, while levelOne and Two ids are numbers
                relationship.TargetType === 'Document' ||
                relationship.TargetType === 'Global'
                  ? relationship.TargetValue
                  : Number(relationship.TargetValue),
            })),
        }))
      : [];
  } catch (error) {
    console.log('getRelatedContents ERROR', error);
    return [];
  }
}

// pages: doc.contents.map((page) => {
//   let newHTML = page.content;

//   if (newHTML.includes("https://adminwordflow.sharepoint.com")) {
//     newHTML = newHTML.replace(/https:[/][/]adminwordflow[.]sharepoint[.]com[/]sites[/]1WordFlow-Sandbox[/]PublishingImages[/]/gi, `${config.WordPressURL}wp-content/uploads/2021/05/`)
//   }

//   return ({
//     HTMLContent: newHTML,
//     documentId: page.doc_id,
//     documentName: page.doc_name,
//     id: page.page_id,
//     parentId: page.page_parent_id,
//     tags: page.tags,
//     title: page.title.replace(`&#8211;`, '-').replace(
//       `&#8217;`,
//       `'` // logic to properly replace single quote and dash
//     ),
//   });
// }),

//#endregion

//#region Document Reducer

export const getDocumentBySlug = async (slug, key) => {
  try {
    loadText('Selected Document...');
    let document = null;

    const slugRes = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_DOCUMENT_BY_SLUG(slug),
      headers: SP_HEADER(key),
    });

    // if user tries to open document ID from URL somehow
    if (
      slugRes.data.message === 'There is no such url' ||
      slugRes.data.status !== 200
    ) {
      const IDRes = await axios({
        method: 'GET',
        baseURL: BASE_URL,
        url: API_DOCUMENT_BY_ID(slug.toUpperCase()),
        headers: SP_HEADER(key),
      });

      if (IDRes.status === 200) {
        document =
          IDRes.data.data && IDRes.data.data.length > 0
            ? IDRes.data.data[0]
            : null;
      }
    } else {
      document =
        slugRes.data.data && slugRes.data.data.length > 0
          ? slugRes.data.data[0]
          : null;
    }

    if (document === null) throw new Error('Document not found');

    const levels = await getDocumentLevels(document);

    if (!levels || !levels.levelOne || !levels.levelTwo)
      throw new Error('Mandatory Levels not found');

    const previousVersionsList = await getPreviousVersions(document, key);

    return {
      id: document.doc_id,
      title: document.title,
      slug: document.slug,
      fileName: document.filename,
      levelOne: levels.levelOne,
      levelTwo: levels.levelTwo,
      levelThree: levels.levelThree,
      levelFour: levels.levelFour,
      version:
        !document.document_version || document.document_version === '-'
          ? null
          : Number(document.document_version),
      status: document.status,
      customTags: document.keywords ? document.keywords.split(',') : [],

      newVersionId: document.new_version_id,
      previousVersionsList: previousVersionsList,
      supersededDate: document.supersededDate
        ? new Date(document.supersededDate)
        : null,
      // publishedDate: new Date(document.PublishedDate),
      // regCity specific
      versionDate: document.version_date
        ? new Date(document.version_date)
        : null,
      registrationDate: document.registration_date
        ? new Date(document.registration_date)
        : null,
      summary: await getDocSummary(key, document.doc_id),
    };
  } catch (e) {
    console.log('ERROR ON getDocumentBySlug', e);
    return 'Error';
  }
};

/**
 * Fetch previous versions from WorkingDocuments list by document ID.
 * (Used if a current document is set before all the documents have been loaded.)
 * @param {object} doc The document to be checked.
 * @param {string} key SharePoint Access key.
 * @returns The array of superseded documents
 */
export const getPreviousVersions = async (doc, key) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_DOCUMENT_BY_NEW_VERSION_ID(doc.doc_id),
      headers: SP_HEADER(key),
    });

    // request returned at least one item
    if (
      apiResponse &&
      apiResponse.data &&
      apiResponse.data.data &&
      apiResponse.data.data.length > 0
    ) {
      const supersededDocuments = apiResponse.data.data;
      const documents = [];

      for (const document of supersededDocuments) {
        const levels = await getDocumentLevels(document);

        if (!levels || !levels.levelOne || !levels.levelTwo) {
          console.log('Mandatory Levels not found for document', document);
        } else {
          documents.push({
            id: document.doc_id,
            title: document.title,
            slug: document.slug,
            fileName: document.filename,
            levelOne: levels.levelOne,
            levelTwo: levels.levelTwo,
            levelThree: levels.levelThree,
            levelFour: levels.levelFour,
            version:
              !document.document_version || document.document_version === '-'
                ? null
                : Number(document.document_version),
            customTags: document.keywords ? document.keywords.split(',') : [],
            status: document.status,
            // versioning
            newVersionId: document.new_version_id,
            previousVersionsList: [],
            supersededDate: document.supersededDate
              ? new Date(document.supersededDate)
              : null,
            // publishedDate: new Date(document.PublishedDate),
            // regCity specific
            versionDate: document.version_date
              ? new Date(document.version_date)
              : null,
            registrationDate: document.registration_date
              ? new Date(document.registration_date)
              : null,
          });
        }
      }
      return documents;
    } else {
      return [];
    }
  } catch (error) {
    console.log('EXC in getPreviousVersions', error.message);
    return [];
  }
};

export const getPagesByDocumentID = async (docID, docSlug, key) => {
  try {
    loadText('Document pages...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_PAGES_BY_DOC(docID),
      headers: SP_HEADER(key),
    });

    const res = apiResponse.data.data;

    // console.log('getPagesByDocumentID', res);

    return res && res.length > 0
      ? res.map((page) => {
          if (page.content.includes('https://adminwordflow.sharepoint.com')) {
            // replace images location
            page.content = page.content.replace(
              new RegExp(clearString(IMAGES_FOLDER), 'gi'),
              `${config.WordPressURL}wp-content/uploads/2022/03/`
            );
          }

          return {
            id: page.page_id,
            title: page.title,
            slug: page.slug,
            parentId:
              page.page_parent_id && page.page_parent_id.length > 0
                ? page.page_parent_id
                : null,
            tags: page.tags,
            documentName: page.doc_name,
            documentId: page.doc_id,
            documentSlug: docSlug,
            HTMLContent: page.content.replace(/href="#/gi, 'href="/app'),
          };
        })
      : [];
  } catch (e) {
    console.log('Error while extracting document pages', e);
    return [];
  }
};

/**
 * Get a specific level item via its Lookup id
 * @param {number} id The id of the level within the list
 * @param {string} level The list to be queried
 * @param {string} key SharePoint Access key.
 * @returns The Level object with title and sort.
 */
const getDocumentLevelById = async (id, level, key) => {
  try {
    const res = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_GET_LIST_BY_ID(level, id),
      headers: SP_HEADER(key),
    });

    if (res.status === 200 && res.data.data.length > 0) {
      const LevelsResponse = res.data.data;

      // select the according level if api returns the whole list
      const Level =
        LevelsResponse.length === 1
          ? LevelsResponse[0]
          : LevelsResponse.filter((level) => Number(level.id) === id)[0];

      return {
        ID: Number(Level.id),
        Title: Level.title,
        Sort: Level.sort,
      };
    } else return null;
  } catch (e) {
    console.log('Error while trying to fetch level', e);
    return null;
  }
};

// Generate navigation based on pages
export const generateNavigation = (pages) => {
  try {
    const navigation = [];
    pages.forEach((page) => {
      const hasParent = page.parentId !== null;
      const hasGrandparent =
        hasParent &&
        pages.filter((parentPage) => parentPage.id === page.parentId)[0]
          .parentId !== null;
      // H1 only
      if (!hasParent) {
        const item = {
          id: page.id,
          slug: page.slug,
          title: page.title,
          children: [],
          content: page.HTMLContent,
        };
        navigation.push(item);
      } // H3 only
      else if (hasParent && hasGrandparent) {
        // find gparent index
        const H1 = navigation.filter(
          (grandParentItem) =>
            grandParentItem.id ===
            pages.filter((parentPage) => parentPage.id === page.parentId)[0]
              .parentId
        )[0];
        if (H1) {
          const H1Index = navigation.indexOf(H1);
          // find parent index in gparent
          const H2 = navigation[H1Index].children.filter(
            (h2) => h2.id === page.parentId
          )[0];
          const H2Index = navigation[H1Index].children.indexOf(H2);

          const H3 = {
            id: page.id,
            slug: page.slug,
            title: page.title,
            content: page.HTMLContent,
          };

          // insert
          navigation[H1Index].children[H2Index].children.push(H3);
        }
      } else {
        const parentItem = navigation.filter(
          (parentPage) => parentPage.id === page.parentId
        )[0];
        if (parentItem) {
          const child = {
            id: page.id,
            slug: page.slug,
            title: page.title,
            content: page.HTMLContent,
            children: [],
          };
          const index = navigation.indexOf(parentItem);
          navigation[index].children.push(child);
        }
      }
    });
    return navigation;
  } catch (error) {
    console.log('ERROR', error);
  }
};

export const getRelatedDocuments = async (docId, key) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_RELATED_DOCUMENT + docId,
      headers: SP_HEADER(key),
    });

    const data = apiResponse.data.data;

    if (data && data.length > 0) {
      const documents = store.getState().client.documents;

      const relatedDocuments = data.map((relatedDocument) => ({
        isParent: docId === relatedDocument.parent_id,
        doc1Title: documents.filter(
          (doc) => doc.id === relatedDocument.parent_id
        )[0]
          ? documents.filter((doc) => doc.id === relatedDocument.parent_id)[0]
              .title
          : '',
        doc1Id: relatedDocument.parent_id,
        doc1Slug: getSlugFromIDs(relatedDocument.parent_id).docSlug,
        doc2Title: relatedDocument.title,
        doc2Id: relatedDocument.child_id,
        doc2Slug: getSlugFromIDs(relatedDocument.child_id).docSlug,
      }));

      return relatedDocuments.filter((doc) => doc.doc1Title.length > 0);
    } else return [];
  } catch (error) {
    console.log('EXC in getRelatedDocuments', error.message);
    return [];
  }
};

export const getRelatedSections = async (pageId, key) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_RELATED_SECTION + pageId,
      headers: SP_HEADER(key),
    });

    if (
      apiResponse &&
      apiResponse.data.data &&
      apiResponse.data.data.length >= 0
    )
      return apiResponse.data.data.map((relatedPage) => ({
        RelatedDocumentId: relatedPage.related_document_id,

        RelatedDocumentSlug: getSlugFromIDs(relatedPage.related_document_id)
          .docSlug,

        RelatedPageId: relatedPage.related_page_id,

        RelatedPageSlug: getSlugFromIDs(null, relatedPage.related_page_id)
          .pageSlug,

        RelatedPageTitle: relatedPage.related_page_title,
      }));
    else return [];
  } catch (error) {
    console.log('EXC in getRelatedSections', error.message);
    return [];
  }
};

// Logic to only save unique relationships (if empty parent and child are both related to same page)
export const getRelationships = async (page, documentPages, spKey) => {
  if (page && documentPages && documentPages.length > 1) {
    let parentRelatedPagesList = [];
    // check if parent is empty and if it is, fetch its related pages too
    if (
      page.parentId !== null && // has a parent page
      documentPages.filter(
        (documentPage) => documentPage.parentId === page.parentId
      )[0].id === page.id && // is the first child of parent
      !pageHasContent(
        // parent page is empty
        documentPages.filter(
          (documentPage) => documentPage.id === page.parentId
        )[0].HTMLContent
      )
    )
      parentRelatedPagesList = await getRelatedSections(page.parentId, spKey);

    const relatedPages = await getRelatedSections(page.id, spKey);

    const allRelations = [...parentRelatedPagesList, ...relatedPages];
    const uniqueIDs = [];

    allRelations.forEach((relation) => uniqueIDs.push(relation.RelatedPageId));

    const uniqueRelations = [];

    uniqueIDs
      .filter((value, index) => uniqueIDs.indexOf(value) === index)
      .forEach((uniqueRelatedPageID) =>
        uniqueRelations.push(
          allRelations.filter(
            (relPages) => relPages.RelatedPageId === uniqueRelatedPageID
          )[0]
        )
      );

    return uniqueRelations;
  } else if (page && documentPages && documentPages.length === 1) {
    const relatedPages = await getRelatedSections(page.id, spKey);
    return relatedPages;
  } else return null;
};

//#endregion

//#region User Reducer

export const logInFromToken = async () => {
  try {
    // console.log('logInFromToken');
    const res = await axios.post(AUTH_BASE + REFRESH_TOKEN_API, null, {
      headers: HEADER,
    });

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;

      // console.log('login', res);

      return data.succeeded === true
        ? data.data
        : {
            success: false,
            reason: data.message,
            request: LOG_IN,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: LOG_IN,
    };
  }
};

export const tryLogin = async (email, password) => {
  try {
    const data = {
      email: email,
      password: password,
    };

    const res = await axios.post(
      AUTH_BASE + AUTHENTICATE,
      JSON.stringify(data),
      {
        headers: HEADER,
      }
    );

    if (res && res.data) {
      const { data } = res;

      // console.log('login', res);

      return data.succeeded === true
        ? data.data
        : {
            success: false,
            reason: data.message,
            userID: data.data && data.data.id ? data.data.id : null,
            request: LOG_IN,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: LOG_IN,
    };
  }
};

export const revokeToken = async () => {
  try {
    // console.log('logInFromToken');
    const res = await axios.post(
      AUTH_BASE + LOG_OUT_API,
      JSON.stringify({
        token: null,
      }),
      {
        headers: HEADER,
      }
    );

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;

      // console.log('login', res);

      return data.succeeded === true
        ? data
        : {
            success: false,
            reason: data.message,
            request: LOG_OUT,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: LOG_OUT,
    };
  }
};

export const sendCancelReason = async (reasonObj) => {
  try {
    const token = store.getState().user.user.token;
    const userID = store.getState().user.user.userID;

    reasonObj = {
      userId: userID,
      ...reasonObj,
    };

    const res = await axios.post(
      AUTH_BASE + CANCEL_REASON_API,
      JSON.stringify(reasonObj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log(res);

    if (res && res.data) {
      const { data } = res;

      if (data.succeeded === true) {
        notification('success', 'Thank you for your feedback.');
      } else if (
        typeof data === 'string' &&
        data.match('The token is expired')
      ) {
        TokenExpiredNotification();
      }
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // if (e.response || e.message) {
    //   const reason = !e.response.data.Message || !e.message ? e.response.data : e.response ? e.response.data.Message : e.message;
    //   if (reason.match('The token is expired')) {
    //     TokenExpiredNotification();
    //   } else {
    //     notification('info', 'Sorry, could not save your response.', reason);
    //   }
    // }
    console.log('Error saving reason for cancellation.', e);
  }
};

export const forgotPassword = async (email) => {
  try {
    const res = await axios.post(
      AUTH_BASE + FORGOT_PASSWORD,
      JSON.stringify({
        email: email,
      }),
      {
        headers: HEADER,
      }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            email: data.data,
            sent: true,
            reason: null,
          }
        : {
            email: email,
            sent: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      email: email,
      sent: false,
      reason: e.response ? e.response.data.Message : e.message,
    };
  }
};

export const resetPassword = async (
  email,
  token,
  password,
  confirmPassword
) => {
  try {
    const data = {
      email: email,
      token: token,
      password: password,
      confirmPassword: confirmPassword,
    };

    const res = await axios.post(
      AUTH_BASE + RESET_PASSWORD,
      JSON.stringify(data),
      {
        headers: HEADER,
      }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            message:
              'Password Reset Successful! You may login with your new password.',
            success: true,
            reason: null,
          }
        : {
            success: false,
            reason: data.message,
            request: RESET_PASS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('Error', e.response);
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: RESET_PASS,
    };
  }
};

export const changePassword = async (passObj, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CHANGE_PASSWORD_API,
      JSON.stringify(passObj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('changePassword', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: UPDATE_PASS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('Error', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: UPDATE_PASS,
    };
  }
};

export const updateUser = async (user, token) => {
  try {
    const res = await axios.put(
      AUTH_BASE + UPDATE_USER_API(user.id),
      JSON.stringify(user),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('res update user', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.Message,
            request: UPDATE_USER,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('error', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: UPDATE_USER,
    };
  }
};

export const register = async (user) => {
  try {
    const res = await axios.post(
      AUTH_BASE + REGISTER_API,
      JSON.stringify(user),
      {
        headers: HEADER,
      }
    );

    // console.log('register', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            userID: data.data,
            firstName: user.firstName,
            email: user.email,
            success: true,
            reason: null,
            request: REGISTER,
          }
        : {
            success: false,
            reason: data.message,
            request: REGISTER,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: REGISTER,
    };
  }
};

export const resendConfirmEmail = async (userId, email) => {
  try {
    const res = await axios.get(AUTH_BASE + RESEND_EMAIL_API(userId), {
      headers: HEADER,
    });

    // console.log('resend confirm email', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            email: email,
            success: true,
            reason: null,
            request: RESEND_EMAIL,
          }
        : {
            success: false,
            reason: data.message,
            request: RESEND_EMAIL,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: RESEND_EMAIL,
    };
  }
};

export const confirmEmail = async (userId, code) => {
  try {
    const res = await axios.get(
      AUTH_BASE + CONFIRM_EMAIL_ENDPOINT(userId, code),
      {
        headers: HEADER,
      }
    );

    // console.log('confirm', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            reason: null,
          }
        : {
            success: false,
            reason: data.message,
            request: CONFIRM_EMAIL,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: CONFIRM_EMAIL,
    };
  }
};

export const createBookmark = async (bookmarkObj, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CREATE_BOOKMARK_API,
      JSON.stringify(bookmarkObj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            newBookmark: {
              id: data.data.id,
              page: data.data.pageId,
              pageTitle: data.data.pageTitle,
              document: data.data.documentId,
            },
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: CREATE_BOOKMARK,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: CREATE_BOOKMARK,
    };
  }
};

export const deleteBookmark = async (bookmark, token) => {
  try {
    const res = await axios.delete(AUTH_BASE + DELETE_BOOKMARK_API(bookmark), {
      headers: AUTH_HEADER(token),
    });

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: DELETE_BOOKMARK,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: DELETE_BOOKMARK,
    };
  }
};

export const deleteBookmarks = async (bookmarksList, token) => {
  try {
    const data = bookmarksList.map((bookmark) => bookmark.id);

    const res = await axios.post(
      AUTH_BASE + DELETE_BOOKMARKS_API,
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: DELETE_BOOKMARKS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: DELETE_BOOKMARKS,
    };
  }
};

export const getUserBookmarks = async (userId, token) => {
  try {
    const res = await axios.get(AUTH_BASE + GET_USER_BOOKMARKS(userId), {
      headers: AUTH_HEADER(token),
    });

    // console.log('get bookmarks', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? data.data.map((bookmark) => ({
            id: bookmark.id,
            page: bookmark.pageId,
            pageTitle: bookmark.pageTitle,
            document: bookmark.documentId,
          }))
        : {
            success: false,
            reason: data.message,
            request: GET_BOOKMARKS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log('getUserBookmarks', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: GET_BOOKMARKS,
    };
  }
};

export const createComment = async (commentObj) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CREATE_COMMENT_API,
      JSON.stringify(commentObj),
      {
        headers: HEADER,
      }
    );

    console.log('comments', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.succeeded,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      !e.response.data.Message || !e.message
        ? e.response.data
        : e.response
        ? e.response.data.Message
        : e.message
    );
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
    };
  }
};

// invite user to corporate account
export const inviteUser = async (newUser, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + INVITE_USER_API,
      JSON.stringify(newUser),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('invite user', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            newUser: {
              id: data.data,
              name: `${newUser.firstName} ${newUser.lastName}`,
              email: newUser.email,
              role: newUser.role === 'CorporateAdmin' ? 'Admin' : 'Basic',
              isAdmin: newUser.role === 'CorporateAdmin' ? true : false,
              accountActive: true,
            },
            success: true,
            reason: null,
            request: INVITE_USER,
          }
        : {
            success: false,
            reason: data.message,
            request: INVITE_USER,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: INVITE_USER_ERROR,
    };
  }
};

export const getUserByClientId = async (clientId, token) => {
  try {
    const res = await axios.get(
      AUTH_BASE + GET_USERS_BY_CLIENT_ID_API(clientId),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('getUserByClientId', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? data.data.map((userByClient) => ({
            id: userByClient.id,
            name: `${userByClient.firstName} ${userByClient.lastName}`,
            email: userByClient.email,
            role: userByClient.roles.includes('CorporateAdmin')
              ? 'Admin'
              : 'Viewer',
            isAdmin: userByClient.roles.some((role) => {
              if (role === 'CorporateAdmin') return true;
              else return false;
            }),
            accountActive: userByClient.active,
            ...userByClient.userByClient,
          }))
        : {
            success: false,
            reason: data.message,
            request: GET_USERS_BY_CLIENT_ID,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('getUserByClientId ERROR', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: GET_USERS_BY_CLIENT_ID,
    };
  }
};

export const totalUsersByClient = async (clientId, token) => {
  try {
    const res = await axios.get(
      AUTH_BASE + GET_USERS_BY_CLIENT_ID_API(clientId),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('getUserByClientId', res);

    if (res && res.data) {
      const { data } = res;
      return res.data.succeeded === true
        ? data.data
        : {
            success: false,
            reason: res.message,
            request: GET_USERS_BY_CLIENT_ID,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('getUserByClientId ERROR', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: GET_USERS_BY_CLIENT_ID,
    };
  }
};

export const deleteUser = async (clientId, userId, token) => {
  try {
    const res = await axios.delete(
      AUTH_BASE + DELETE_USER_CLIENT_API(clientId, userId),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('delete user', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            request: DELETE_USER_CLIENT,
          }
        : {
            success: false,
            reason: data.message,
            request: DELETE_USER_CLIENT,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('delete user', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: DELETE_USER_CLIENT_API,
    };
  }
};

export const updateClientRole = async (data, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + UPDATE_CLIENT_ROLE_API,
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('update role', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            reason: data.message,
            request: UPDATE_CLIENT_ROLE,
          }
        : {
            success: false,
            reason: data.message,
            request: UPDATE_CLIENT_ROLE,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log('error', e);
    return {
      success: false,
      reason:
        !e.response.data.message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: UPDATE_CLIENT_ROLE,
    };
  }
};

export const reinviteUser = async (email, clientId, token) => {
  try {
    const data = {
      email: email,
      clientId: clientId,
    };

    const res = await axios.post(
      AUTH_BASE + REINVITE_USER_API,
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('reinvite user', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            reason: data.message,
            request: REINVITE_USER,
          }
        : {
            success: false,
            reason: data.message,
            request: REINVITE_USER,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(e);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: REINVITE_USER_ERROR,
    };
  }
};

export const sendEnquiry = async (quote) => {
  try {
    const res = await axios.post(
      AUTH_BASE + SEND_ENQUIRY_API,
      quote
      // {
      //   headers: AUTH_HEADER(token),
      // }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            reason: data.message,
            request: SEND_ENQUIRY,
          }
        : {
            success: false,
            reason: data.message,
            request: SEND_ENQUIRY,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(e);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: SEND_ENQUIRY,
    };
  }
};

export const getDocSummary = async (key, id) => {
  try {
    loadText('Document Summary...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_GET_DOC_SUMMARY(id),
      headers: SP_HEADER(key),
    });

    const data = apiResponse.data.data;

    return data.filter((doc) => doc.documentId === id)[0].summary;
  } catch (error) {
    console.log('Error in get Sumary', error.message);
    return null;
  }
};

export const getUserClient = async (key, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: UPDATE_CLIENT_API(id),
      headers: AUTH_HEADER(key),
    });

    const data = apiResponse.data.data;
    return data;
  } catch (error) {
    console.log('Error getting user client', error.message);
    return null;
  }
};

export const createClientSubscription = async (clientObj, token) => {
  try {
    const data = {
      clientId: clientObj.clientId,
      paddlePlanType: clientObj.paddlePlanType,
      paddleCustomerId: clientObj.paddleCustomerId,
      paddleSubscriptionId: clientObj.paddleSubscriptionId,
      paddlePriceId: clientObj.paddlePriceId,
      startDate: clientObj.startDate,
      endDate: clientObj.endDate,
    };

    const res = await axios.post(
      AUTH_BASE + CREATE_CLIENT_SUBSCRIPTION_API,
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            data: data.data,
            request: CREATE_CLIENT_SUBSCRIPTION,
          }
        : {
            success: false,
            reason: data.Message,
            request: CREATE_CLIENT_SUBSCRIPTION,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: CREATE_CLIENT_SUBSCRIPTION,
    };
  }
};

export const updateClientSubscription = async (clientObj, token) => {
  try {
    const data = {
      clientId: clientObj.clientId ? clientObj.clientId : '',
      paddlePlanType: clientObj.paddlePlanType ? clientObj.paddlePlanType : '',
      paddleCustomerId: clientObj.paddleCustomerId
        ? clientObj.paddleCustomerId
        : '',
      paddleSubscriptionId: clientObj.paddleSubscriptionId
        ? clientObj.paddleSubscriptionId
        : '',
      paddlePriceId: clientObj.paddlePriceId ? clientObj.paddlePriceId : '',
      planId: clientObj.planId ? clientObj.planId : '',
      startDate: clientObj.startDate ? clientObj.startDate : '',
      endDate: clientObj.endDate ? clientObj.endDate : '',
      userId: clientObj.userId ? clientObj.userId : '',
    };

    const res = await axios.put(
      AUTH_BASE + UPDATE_CLIENT_SUBSCRIPTION_API(clientObj.clientId),
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            data: data.data,
            request: SUBSCRIPTION_UPDATE,
          }
        : {
            success: false,
            reason: data.Message,
            request: SUBSCRIPTION_UPDATE,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: SUBSCRIPTION_UPDATE,
    };
  }
};

export const getUserPlans = async (token) => {
  try {
    const res = await axios.get(AUTH_BASE + GET_PLANS, {
      headers: AUTH_HEADER(token),
    });

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? // success: true,
          data.data
        : // request: GET_USER_PLANS,

          {
            success: false,
            reason: data.Message,
            request: GET_USER_PLANS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: GET_USER_PLANS,
    };
  }
};

export const getUserUsage = async (token, userId) => {
  try {
    const res = await axios.get(AUTH_BASE + USAGE_BY_USER_ID_API(userId), {
      headers: AUTH_HEADER(token),
    });

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? // success: true,
          data.data
        : // request: GET_USER_PLANS,

          {
            success: false,
            reason: data.Message,
            request: GET_USER_USAGE,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: GET_USER_USAGE,
    };
  }
};

export const updateUserUsage = async (obj, token) => {
  try {
    // const data = {
    //   userId: obj.userId,
    //   clientId: obj.clientId,
    //   pageViewUsed: obj.pageViewUsed ? obj.pageViewUsed : 0,
    //   queryUsed: obj.queryUsed ? obj.queryUsed : 0,
    //   summaryUsed: obj.summaryUsed ? obj.summaryUsed : 0,
    //   shareUsed: obj.shareUsed ? obj.shareUsed : 0,
    //   additionalQueryUsed: obj.additionalQueryUsed
    //     ? obj.additionalQueryUsed
    //     : 0,
    // };

    const res = await axios.post(
      AUTH_BASE + UPDATE_USER_USAGE_API,
      JSON.stringify(obj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return res.status === 200
        ? {
            success: true,
            data: data.data,
            request: UPDATE_USER_USAGE,
          }
        : {
            success: false,
            reason: data.Message,
            request: UPDATE_USER_USAGE,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: UPDATE_USER_USAGE,
    };
  }
};

export const createNewQuery = async (token, query) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CREATE_NEW_QUERY_API,
      JSON.stringify(query),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('createNewQuery', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.succeeded,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      !e.response.data.Message || !e.message
        ? e.response.data
        : e.response
        ? e.response.data.Message
        : e.message
    );
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
    };
  }
};

export const updateQuery = async (token, query) => {
  try {
    const res = await axios.put(
      AUTH_BASE + UPDATE_QUERY_API(query.id),
      JSON.stringify(query),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('updateQuery', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.succeeded,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      !e.response.data.Message || !e.message
        ? e.response.data
        : e.response
        ? e.response.data.Message
        : e.message
    );
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
    };
  }
};

export const deleteQuery = async (token, queryId) => {
  try {
    const res = await axios.delete(AUTH_BASE + DELETE_QUERY_API(queryId), {
      headers: AUTH_HEADER(token),
    });

    // console.log('query', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: DELETE_BOOKMARK,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: DELETE_BOOKMARK,
    };
  }
};

export const getUserQueries = async (token, userId) => {
  try {
    const res = await axios.get(AUTH_BASE + GET_QUERY_BY_USER_ID_API(userId), {
      headers: AUTH_HEADER(token),
    });

    // console.log('getUserQueries', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? // success: true,
          data.data
        : // request: GET_USER_PLANS,

          {
            success: false,
            reason: data.Message,
            request: GET_QUERIES,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: GET_USER_PLANS,
    };
  }
};

export const getQueryMessages = async (token, queryId) => {
  try {
    const res = await axios.get(
      AUTH_BASE + GET_QUERY_MESSAGES_BY_QUERY_ID_API(queryId),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('getQueryMessages', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? // success: true,
          data.data
        : // request: GET_USER_PLANS,

          {
            success: false,
            reason: data.Message,
            request: GET_MESSAGES,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: GET_MESSAGES,
    };
  }
};

export const createNewMessage = async (token, message) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CREATE_NEW_MESSAGE_API,
      JSON.stringify(message),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('createNewMessage', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.data,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      !e.response.data.Message || !e.message
        ? e.response.data
        : e.response
        ? e.response.data.Message
        : e.message
    );
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
    };
  }
};

export const updateMessageResponse = async (token, messageId, response) => {
  try {
    const res = await axios.put(
      AUTH_BASE + UPDATE_MESSAGES_RESPONSE_API(messageId),
      JSON.stringify(response),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('updateMessageResponse', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.succeeded,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      !e.response.data.Message || !e.message
        ? e.response.data
        : e.response
        ? e.response.data.Message
        : e.message
    );
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
    };
  }
};

export const createAdditionalPurchase = async (obj, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + BUY_ADDITIONAL_QUERIES_API,
      JSON.stringify(obj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('createAdditionalPurchase', res);

    if (res && res.data) {
      const { data } = res;
      return res.status === 200
        ? {
            success: true,
            data: data.data,
            request: BUY_ADDITIONAL_QUERIES,
          }
        : {
            success: false,
            reason: data.Message,
            request: BUY_ADDITIONAL_QUERIES,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        !e.response.data.Message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: BUY_ADDITIONAL_QUERIES,
    };
  }
};

//#endregion
